import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { FireIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL, getUploadUrl } from '../config/api';
import './css/productdetails.css';
import FooterNote from './FooterNote';

const ProductDetails = () => {
  const { productId, category, branchId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  
  const [product, setProduct] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedAdditionIds, setSelectedAdditionIds] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const isBranchProduct = !!branchId;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);

        const productRes = await axios.get(`${API_URL}/api/products/${productId}`);
        setProduct(productRes.data);

        const sizesRes = await axios.get(`${API_URL}/api/products/${productId}/sizes`);
        const activeSizes = sizesRes.data.filter(s => s.is_active).sort((a, b) => a.display_order - b.display_order);
        setSizes(activeSizes);

        if (activeSizes.length > 0) {
          setSelectedSize(activeSizes[0]);
        }
        setSelectedAdditionIds([]);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const handleBack = () => {
    if (isBranchProduct) {
      navigate(`/branch/${branchId}/category/${category}`);
    } else {
      navigate(`/${category}`);
    }
  };

  if (loading) {
    return (
      <section className='main-view'>
        <div className='container-fluid'>
          <div className='loading-message'>
            <p>{t('products.loading') || 'Loading...'}</p>
          </div>
        </div>
        <FooterNote />
      </section>
    );
  }

  if (!product) {
    return (
      <section className='main-view'>
        <div className='container-fluid'>
          <div className='error-message'>
            <p>{t('products.notFound') || 'Product not found'}</p>
            <button onClick={handleBack}>{t('nav.back') || 'Back'}</button>
          </div>
        </div>
        <FooterNote />
      </section>
    );
  }

  const productName = language === 'ar' ? product.name_ar : product.name_en;
  const productDescription = language === 'ar' ? product.description_ar : product.description_en;
  const productIngredients = language === 'ar' 
    ? (Array.isArray(product.ingredients_ar) ? product.ingredients_ar : [])
    : (Array.isArray(product.ingredients_en) ? product.ingredients_en : []);
  
  const displayPrice = selectedSize 
    ? parseFloat(selectedSize.price)
    : (product.price ? parseFloat(product.price) : 0);

  const baseCalories = selectedSize && selectedSize.calories !== undefined && selectedSize.calories !== null
    ? selectedSize.calories
    : product.calories;

  const activeAdditions = Array.isArray(product.additions)
    ? product.additions.filter((item) => item.is_active)
    : [];
  const productAllergens = Array.isArray(product.allergens)
    ? product.allergens.filter((item) => item.is_active)
    : [];

  const selectedAdditions = activeAdditions.filter((item) => selectedAdditionIds.includes(item.id));
  const additionsPrice = selectedAdditions.reduce((sum, item) => sum + Number(item.price || 0), 0);

  const finalPrice = displayPrice + additionsPrice;
  const displayCalories = Number(baseCalories || 0);

    return (
      <section className='main-view'>
        <section className='product-details'>
          <div className='container-fluid'>
            <div className='product-details-content'>
              {/* Product Image Hero */}
              <div className='product-hero-image'>
                {product.image ? (
                  <img 
                    src={getUploadUrl(product.image)} 
                    alt={productName} 
                    className='hero-image' 
                  />
                ) : (
                  <div className='hero-image-placeholder'>
                    {t('products.noImage') || 'No Image'}
                  </div>
                )}
              </div>
  
              {/* Product Card */}
              <div className='product-card'>
                {/* Header with Back Button */}
                <div className='product-card-header'>
                  <button onClick={handleBack} className='back-btn-circle'>
                    <ArrowLeftIcon className='icon' />
                  </button>
                  <h1 className='product-card-title'>{productName}</h1>
                </div>
  
                {/* Price Display */}
                <div className='product-price-display'>
                  <span className='price-value'>{finalPrice.toFixed(2)}</span>
                  <span className='price-currency'>{t('products.currency') || 'SAR'}</span>
                </div>
  
                {/* Calories + Allergens inline badges */}
                <div className='product-badges-row'>
                  {(baseCalories !== null && baseCalories !== undefined) && (
                    <div className='info-badge calories-badge'>
                      <FireIcon className='badge-svg-icon' />
                      <span className='badge-text'>{displayCalories} {t('products.calorieUnit') || 'Cal'}</span>
                    </div>
                  )}

                  {productAllergens.map((allergen) => (
                    <div key={allergen.id} className='info-badge allergen-badge'>
                      <span className='badge-icon'>{allergen.icon || '⚠️'}</span>
                      <span className='badge-text'>{language === 'ar' ? allergen.name_ar : allergen.name_en}</span>
                    </div>
                  ))}
                </div>
  
                {/* Description */}
                {productDescription && (
                  <div className='product-description'>
                    <h3 className='section-title'>{t('products.description') || 'Description'}</h3>
                    <p className='description-text'>{productDescription}</p>
                  </div>
                )}

                {/* Size Selection */}
                {sizes.length > 0 && (
                  <div className='sizes-selection'>
                    <h3 className='section-title'>{t('products.selectSize') || 'Choose size'}</h3>
                    <p className='section-subtitle'>{t('products.required') || 'Required'}</p>
                    
                    <div className='sizes-radio-list'>
                      {sizes.map((size) => {
                        const sizeName = language === 'ar' ? size.size_name_ar : size.size_name_en;
                        const isSelected = selectedSize && selectedSize.id === size.id;
                        const sizePrice = parseFloat(size.price);
                        
                        return (
                          <label
                            key={size.id}
                            className={`size-radio-option ${isSelected ? 'selected' : ''}`}
                          >
                            <input
                              type='radio'
                              name='size'
                              checked={isSelected}
                              onChange={() => setSelectedSize(size)}
                              className='size-radio-input'
                            />
                            <div className='size-radio-content'>
                              <span className='size-radio-label'>{sizeName}</span>
                              <span className='size-radio-price'>
                                {sizePrice.toFixed(2)} {t('products.currency') || 'SAR'}
                              </span>
                            </div>
                            <span className='size-radio-circle'></span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Additions */}
                {activeAdditions.length > 0 && (
                  <div className='product-additions'>
                    <h3 className='section-title'>{t('products.additions') || 'Additions'}</h3>
                    <p className='section-subtitle'>{t('products.chooseAdditions') || 'You can select more than one addition'}</p>
                    <div className='additions-list'>
                      {activeAdditions.map((addition) => {
                        const name = language === 'ar' ? addition.name_ar : addition.name_en;
                        const checked = selectedAdditionIds.includes(addition.id);
                        return (
                          <label key={addition.id} className={`size-radio-option addition-option ${checked ? 'selected' : ''}`}>
                            <input
                              type='checkbox'
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...selectedAdditionIds, addition.id]
                                  : selectedAdditionIds.filter((id) => id !== addition.id);
                                setSelectedAdditionIds(next);
                              }}
                              className='size-radio-input'
                            />
                            <div className='addition-content'>
                              <div className='addition-texts'>
                                <span className='addition-name'>{name}</span>
                                <span className='addition-calories'>
                                  <FireIcon className='addition-calories-icon' />
                                  {Number(addition.calories || 0)} {t('products.calorieUnit') || 'Cal'}
                                </span>
                              </div>
                              <span className='size-radio-price addition-price'>
                                +{Number(addition.price || 0).toFixed(2)} {t('products.currency') || 'SAR'}
                              </span>
                            </div>
                            <span className='size-radio-circle addition-radio-circle'></span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Ingredients */}
                {productIngredients.length > 0 && (
                  <div className='product-ingredients'>
                    <h3 className='section-title'>{t('products.ingredients') || 'Ingredients'}</h3>
                    <ul className='ingredients-list'>
                      {productIngredients.map((ingredient, index) => (
                        <li key={index}>{ingredient}</li>
                      ))}
                    </ul>
                  </div>
                )}
  
             
              </div>
            </div>
          </div>
        </section>
        <FooterNote />
      </section>
    );
};

export default ProductDetails;

